<template>
  <v-container fluid grid-list-lg>
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />
    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editTermComment="selectedTermComment"
    />

    <v-card :loading="loading" outlined>
      <ListTitle
        :showBackButton="true"
        :access="checkRightStatus(53)"
        title="Term Comments"
        @add="add"
      />

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.search"
              outlined
              prepend-inner-icon="search"
              rounded
              dense
              label="Search comment, student, term, year, class"
              hide-details
              clearable
              @input="debouncedGetList"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.ClassId"
              :items="classes"
              item-text="name"
              item-value="id"
              outlined
              dense
              label="Class"
              hide-details
              clearable
              @change="onClassChange"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.YearId"
              :items="filteredYears"
              item-text="displayName"
              item-value="id"
              outlined
              dense
              label="Year"
              hide-details
              clearable
              @change="onYearChange"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.TermId"
              :items="filteredTerms"
              item-text="displayName"
              item-value="id"
              outlined
              dense
              label="Term"
              hide-details
              clearable
              @change="getList"
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-autocomplete
              v-model="filters.StudentId"
              :items="students"
              item-text="fullName"
              item-value="id"
              outlined
              dense
              label="Student"
              hide-details
              clearable
              @change="getList"
            />
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12" class="d-flex justify-end">
            <v-chip class="mr-2" outlined>
              Total: {{ termComments.length }}
            </v-chip>
            <v-btn small text color="primary" @click="resetFilters">
              Reset Filters
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-data-table
        :headers="headers"
        :items="termComments"
        :loading="loading"
        :sort-by="sortBy"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        mobile-breakpoint="0"
      >
        <template v-slot:item="{ item }">
          <tr class="pointer">
            <td class="text-left">{{ item.createdAt | moment("LL") }}</td>

            <td class="text-left">
              <div v-if="item.Term && item.Term.Year && item.Term.Year.Class">
                {{ item.Term.Year.Class.name }}
              </div>
              <div v-else>N/A</div>
            </td>

            <td class="text-left">
              <div v-if="item.Term && item.Term.Year">
                {{ item.Term.Year.name }}
              </div>
              <div v-else>N/A</div>
            </td>

            <td class="text-left">
              <div v-if="item.Term">{{ item.Term.name }}</div>
              <div v-else>N/A</div>
            </td>

            <td class="text-left">
              <div v-if="item.Student">{{ item.Student.fullName }}</div>
              <div v-else>N/A</div>
            </td>

            <td class="text-left comment-cell">
              {{ item.comment }}
            </td>

            <td class="justify-end align-center text-right">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" icon>
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                </template>

                <v-list class="menu-button">
                  <v-list-item
                    v-if="checkRightStatus(55)"
                    @click="editComment(item)"
                  >
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="checkRightStatus(56)"
                    @click="deleteComment(item)"
                  >
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">
          No matching comments found.
        </v-alert>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import commentService from "../service";
import classService from "@/modules/Classes/service";
import yearService from "@/modules/ReportCard/Year/service";
import termService from "@/modules/ReportCard/Terms/service";
import studentService from "@/modules/Student/service";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");

export default {
  name: "term-comments-list",
  components: {
    Create,
    Edit,
  },
  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedTermComment: null,
      headers: [
        {
          text: "Created At",
          align: "left",
          value: "createdAt",
          sortable: true,
        },
        {
          text: "Class",
          align: "left",
          value: "Term.Year.Class.name",
          sortable: false,
        },
        {
          text: "Year",
          align: "left",
          value: "Term.Year.name",
          sortable: false,
        },
        {
          text: "Term",
          align: "left",
          value: "Term.name",
          sortable: false,
        },
        {
          text: "Student",
          align: "left",
          value: "Student.fullName",
          sortable: false,
        },
        {
          text: "Comment",
          align: "left",
          value: "comment",
          sortable: false,
        },
        {
          text: "",
          value: "action",
          sortable: false,
        },
      ],
      termComments: [],
      classes: [],
      years: [],
      terms: [],
      students: [],
      filters: {
        search: null,
        ClassId: null,
        YearId: null,
        TermId: null,
        StudentId: null,
      },
      loading: true,
      page: 1,
      sortBy: "createdAt",
      itemsPerPage: 25,
      debounceTimer: null,
    };
  },
  created() {
    this.loadFilters();
    this.getList();
    document.title = "taleem - Term Comments List";
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    filteredYears() {
      let rows = this.years || [];

      if (this.filters.ClassId) {
        rows = rows.filter((row) => row.ClassId === this.filters.ClassId);
      }

      return rows.map((row) => ({
        ...row,
        displayName: row.Class ? `${row.name} , ${row.Class.name}` : row.name,
      }));
    },

    filteredTerms() {
      let rows = this.terms || [];

      if (this.filters.YearId) {
        rows = rows.filter((row) => row.YearId === this.filters.YearId);
      }

      return rows.map((row) => ({
        ...row,
        displayName:
          row.Year && row.Year.Class
            ? `${row.name} , ${row.Year.name} , ${row.Year.Class.name}`
            : row.name,
      }));
    },
  },
  methods: {
    add() {
      this.createDialog = !this.createDialog;
    },

    async loadFilters() {
      try {
        const [classRes, yearRes, termRes, studentRes] = await Promise.all([
          classService.getAll({ limit: 10000 }),
          yearService.getAll({ limit: 10000 }),
          termService.getAll({ limit: 10000 }),
          studentService.getAll({ limit: 10000 }),
        ]);

        this.classes = classRes.data || [];
        this.years = yearRes.data || [];
        this.terms = termRes.data || [];
        this.students = studentRes.data || [];
      } catch (error) {
        console.log("error loading filters", error);
      }
    },

    buildParams() {
      const params = {};

      if (this.filters.search) params.search = this.filters.search;
      if (this.filters.ClassId) params.ClassId = this.filters.ClassId;
      if (this.filters.YearId) params.YearId = this.filters.YearId;
      if (this.filters.TermId) params.TermId = this.filters.TermId;
      if (this.filters.StudentId) params.StudentId = this.filters.StudentId;

      return params;
    },

    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      return commentService
        .getAll(this.buildParams())
        .then((response) => {
          this.loading = false;
          this.termComments = response.data || [];
          return response;
        })
        .catch((error) => {
          this.loading = false;
          console.log("error getting comments", error);
        });
    },

    debouncedGetList() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.getList();
      }, 300);
    },

    onClassChange() {
      this.filters.YearId = null;
      this.filters.TermId = null;
      this.getList();
    },

    onYearChange() {
      this.filters.TermId = null;
      this.getList();
    },

    resetFilters() {
      this.filters = {
        search: null,
        ClassId: null,
        YearId: null,
        TermId: null,
        StudentId: null,
      };
      this.getList();
    },

    editComment(termComment) {
      this.selectedTermComment = termComment;
      this.editDialog = !this.editDialog;
    },

    deleteComment(comment) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          commentService.delete(comment.id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Comment has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

.comment-cell {
  max-width: 420px;
  white-space: normal;
  word-break: break-word;
}
</style>